import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { auditService } from "../services/auditService";

/**
 * Hook para manejar alertas del sistema de serialización
 * Proporciona funcionalidad completa para gestión de alertas con auditoría automática
 */
export const useSerializationAlerts = () => {
    const { user } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // Tipos de alertas disponibles
    const ALERT_TYPES = useMemo(() => ({
        VERSION_CONFLICT: "version_conflict",
        ROLLBACK_PERFORMED: "rollback_performed",
        VALIDATION_FAILURE: "validation_failure",
        MIGRATION_STARTED: "migration_started",
        MIGRATION_COMPLETED: "migration_completed",
        MIGRATION_FAILED: "migration_failed",
        SCHEMA_UPDATED: "schema_updated",
        ENTITY_SERIALIZED: "entity_serialized",
        ENTITY_DESERIALIZED: "entity_deserialized",
        SYSTEM_ERROR: "system_error"
    }), []);

    // Severidades de alertas
    const ALERT_SEVERITY = useMemo(() => ({
        [ALERT_TYPES.VERSION_CONFLICT]: "warning",
        [ALERT_TYPES.ROLLBACK_PERFORMED]: "info",
        [ALERT_TYPES.VALIDATION_FAILURE]: "error",
        [ALERT_TYPES.MIGRATION_STARTED]: "info",
        [ALERT_TYPES.MIGRATION_COMPLETED]: "success",
        [ALERT_TYPES.MIGRATION_FAILED]: "error",
        [ALERT_TYPES.SCHEMA_UPDATED]: "info",
        [ALERT_TYPES.ENTITY_SERIALIZED]: "success",
        [ALERT_TYPES.ENTITY_DESERIALIZED]: "info",
        [ALERT_TYPES.SYSTEM_ERROR]: "error"
    }), [ALERT_TYPES]);

    /**
     * Log automático de alertas en auditoría
     */
    const logAlertToAudit = useCallback(async (alert) => {
        try {
            await auditService.logUserAction(
                user?.uid || "system",
                "serialization_alert",
                {
                    alertType: alert.type,
                    entityType: alert.entityType,
                    entityId: alert.entityId,
                    message: alert.message,
                    severity: ALERT_SEVERITY[alert.type],
                    timestamp: alert.timestamp
                }
            );
        } catch (error) {
            // console.error("Error logging alert to audit:", error);
        }
    }, [user, ALERT_SEVERITY]);

    /**
     * Agregar una nueva alerta
     */
    const addAlert = useCallback((alert) => {
        const newAlert = {
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            read: false,
            ...alert
        };

        setAlerts(prev => [newAlert, ...prev]);
        setUnreadCount(prev => prev + 1);

        // Log automático en auditoría
        logAlertToAudit(newAlert);
    }, [logAlertToAudit]);

    /**
     * Marcar alerta como leída
     */
    const markAsRead = useCallback((alertId) => {
        setAlerts(prev => 
            prev.map(alert => 
                alert.id === alertId 
                    ? { ...alert, read: true }
                    : alert
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    /**
     * Marcar todas las alertas como leídas
     */
    const markAllAsRead = useCallback(() => {
        setAlerts(prev => prev.map(alert => ({ ...alert, read: true })));
        setUnreadCount(0);
    }, []);

    /**
     * Eliminar una alerta
     */
    const removeAlert = useCallback((alertId) => {
        setAlerts(prev => {
            const alert = prev.find(a => a.id === alertId);
            if (alert && !alert.read) {
                setUnreadCount(count => Math.max(0, count - 1));
            }
            return prev.filter(a => a.id !== alertId);
        });
    }, []);

    /**
     * Limpiar todas las alertas
     */
    const clearAllAlerts = useCallback(() => {
        setAlerts([]);
        setUnreadCount(0);
    }, []);

    /**
     * Filtrar alertas por tipo
     */
    const getAlertsByType = useCallback((type) => {
        return alerts.filter(alert => alert.type === type);
    }, [alerts]);

    /**
     * Filtrar alertas por severidad
     */
    const getAlertsBySeverity = useCallback((severity) => {
        return alerts.filter(alert => ALERT_SEVERITY[alert.type] === severity);
    }, [alerts, ALERT_SEVERITY]);

    /**
     * Obtener alertas no leídas
     */
    const getUnreadAlerts = useCallback(() => {
        return alerts.filter(alert => !alert.read);
    }, [alerts]);

    /**
     * Eventos específicos del sistema de serialización
     */
    const serializationEvents = {
        // Conflicto de versiones
        onVersionConflict: useCallback((entityType, entityId, currentVersion, targetVersion) => {
            addAlert({
                type: ALERT_TYPES.VERSION_CONFLICT,
                entityType,
                entityId,
                message: `Conflicto de versiones detectado en ${entityType} ${entityId}. Versión actual: ${currentVersion}, Versión objetivo: ${targetVersion}`,
                details: { currentVersion, targetVersion }
            });
        }, [addAlert, ALERT_TYPES.VERSION_CONFLICT]),

        // Rollback realizado
        onRollbackPerformed: useCallback((entityType, entityId, fromVersion, toVersion) => {
            addAlert({
                type: ALERT_TYPES.ROLLBACK_PERFORMED,
                entityType,
                entityId,
                message: `Rollback realizado en ${entityType} ${entityId}. De ${fromVersion} a ${toVersion}`,
                details: { fromVersion, toVersion }
            });
        }, [addAlert, ALERT_TYPES.ROLLBACK_PERFORMED]),

        // Fallo de validación
        onValidationFailure: useCallback((entityType, entityId, errors) => {
            addAlert({
                type: ALERT_TYPES.VALIDATION_FAILURE,
                entityType,
                entityId,
                message: `Fallo de validación en ${entityType} ${entityId}. ${errors.length} errores encontrados.`,
                details: { errors }
            });
        }, [addAlert, ALERT_TYPES.VALIDATION_FAILURE]),

        // Migración iniciada
        onMigrationStarted: useCallback((entityType, fromVersion, toVersion, totalEntities) => {
            addAlert({
                type: ALERT_TYPES.MIGRATION_STARTED,
                entityType,
                message: `Migración iniciada para ${entityType}. De ${fromVersion} a ${toVersion}. ${totalEntities} entidades afectadas.`,
                details: { fromVersion, toVersion, totalEntities }
            });
        }, [addAlert, ALERT_TYPES.MIGRATION_STARTED]),

        // Migración completada
        onMigrationCompleted: useCallback((entityType, fromVersion, toVersion, successCount, totalCount) => {
            addAlert({
                type: ALERT_TYPES.MIGRATION_COMPLETED,
                entityType,
                message: `Migración completada para ${entityType}. ${successCount}/${totalCount} entidades migradas exitosamente.`,
                details: { fromVersion, toVersion, successCount, totalCount }
            });
        }, [addAlert, ALERT_TYPES.MIGRATION_COMPLETED]),

        // Migración fallida
        onMigrationFailed: useCallback((entityType, fromVersion, toVersion, error) => {
            addAlert({
                type: ALERT_TYPES.MIGRATION_FAILED,
                entityType,
                message: `Migración fallida para ${entityType}. Error: ${error}`,
                details: { fromVersion, toVersion, error }
            });
        }, [addAlert, ALERT_TYPES.MIGRATION_FAILED]),

        // Esquema actualizado
        onSchemaUpdated: useCallback((entityType, version, changes) => {
            addAlert({
                type: ALERT_TYPES.SCHEMA_UPDATED,
                entityType,
                message: `Esquema actualizado para ${entityType} v${version}. ${changes.length} cambios aplicados.`,
                details: { version, changes }
            });
        }, [addAlert, ALERT_TYPES.SCHEMA_UPDATED]),

        // Entidad serializada
        onEntitySerialized: useCallback((entityType, entityId, version) => {
            addAlert({
                type: ALERT_TYPES.ENTITY_SERIALIZED,
                entityType,
                entityId,
                message: `${entityType} ${entityId} serializado a versión ${version}`,
                details: { version }
            });
        }, [addAlert, ALERT_TYPES.ENTITY_SERIALIZED]),

        // Entidad deserializada
        onEntityDeserialized: useCallback((entityType, entityId, version) => {
            addAlert({
                type: ALERT_TYPES.ENTITY_DESERIALIZED,
                entityType,
                entityId,
                message: `${entityType} ${entityId} deserializado desde versión ${version}`,
                details: { version }
            });
        }, [addAlert, ALERT_TYPES.ENTITY_DESERIALIZED]),

        // Error del sistema
        onSystemError: useCallback((error, context) => {
            addAlert({
                type: ALERT_TYPES.SYSTEM_ERROR,
                message: `Error del sistema: ${error}`,
                details: { error, context }
            });
        }, [addAlert, ALERT_TYPES.SYSTEM_ERROR])
    };

    // Cargar alertas existentes al montar el componente
    useEffect(() => {
        const loadExistingAlerts = async () => {
            setLoading(true);
            try {
                // TODO: Implementar carga de alertas desde el backend
                // const existingAlerts = await serializationService.getAlerts();
                // setAlerts(existingAlerts);
            } catch (error) {
                // console.error("Error loading alerts:", error);
            } finally {
                setLoading(false);
            }
        };

        loadExistingAlerts();
    }, []);

    return {
        // Estado
        alerts,
        unreadCount,
        loading,
        
        // Métodos
        addAlert,
        markAsRead,
        markAllAsRead,
        removeAlert,
        clearAllAlerts,
        getAlertsByType,
        getAlertsBySeverity,
        getUnreadAlerts,
        
        // Eventos específicos
        ...serializationEvents,
        
        // Constantes
        ALERT_TYPES,
        ALERT_SEVERITY
    };
}; 